import React from "react";
import {
  Box, Typography, Stack,
  Button, Divider, Tooltip, Paper,
  Fade,
} from "@mui/material";
import EqualizerIcon from '@mui/icons-material/Equalizer';
import StarOutlineIcon from '@mui/icons-material/StarOutline';

const QuestionFilterComponent = ({
  groupsById,
  selectedVariable,
  preferenceFilter,
  onPreferenceFilterChange,
  ActionMode,
  side,
  selectedFeatureData,
}) => {
  const currentVar = groupsById?.[selectedVariable];
  const firstLikertGridSubVar = currentVar?.type === "likert_grid"
    ? currentVar?.subVariables?.[0]
    : null;
  const [prefLikertGridSubVarId, prefLikertGridMetricRaw] = String(preferenceFilter || "").split("|");

  const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
  const detectSentimentPair = (subVar) => {
    const posKey = toArr(subVar?.polarity?.pos)[0];
    const negKey = toArr(subVar?.polarity?.neg)[0];
    const posLabel = String(subVar?.labels?.[posKey] || subVar?.full_labels?.[posKey] || "").toLowerCase();
    const negLabel = String(subVar?.labels?.[negKey] || subVar?.full_labels?.[negKey] || "").toLowerCase();
    const text = `${posLabel} ${negLabel}`;
    if (text.includes("comfort")) {
      return { posToken: "comfort", posLabel: "Comfort", negToken: "discomfort", negLabel: "Discomfort" };
    }
    return { posToken: "trust", posLabel: "Trust", negToken: "distrust", negLabel: "Distrust" };
  };
  const activeLikertGridSubVar = groupsById?.[prefLikertGridSubVarId] || groupsById?.[firstLikertGridSubVar];
  const sentimentPair = detectSentimentPair(activeLikertGridSubVar);
  const metricIsNegative = ["distrust", "discomfort", "neg", "negative"].includes(String(prefLikertGridMetricRaw || "").toLowerCase());
  const currentLikertGridMetric = metricIsNegative ? sentimentPair.negToken : sentimentPair.posToken;
  const isWindfallGrid = String(selectedVariable || "").toLowerCase() === "windfall";

  const toValueMap = (arr) =>
    new Map((arr || [])
      .filter((r) => r?.key != null)
      .map((r) => [String(r.key), Number(r.value)]));

  const nationalMap = toValueMap(selectedFeatureData?.national);

  const rankKey = (key) => {
    const k = String(key);
    const n = nationalMap.get(k);
    if (Number.isFinite(n)) return n;
    return -Infinity;
  };

  const orderedKeys = (keys, varConfig = currentVar) =>
    varConfig?.fixedOrder ? [...keys] : sortKeysByArea(keys, varConfig);

  const sortKeysByArea = (keys = []) => {
    if (currentVar?.["type"] === "likert") {
      const toArrPol = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
      const pos = toArrPol(currentVar?.polarity?.pos);
      const neu = toArrPol(currentVar?.polarity?.neu);
      const neg = toArrPol(currentVar?.polarity?.neg);
      const dk = toArrPol(currentVar?.polarity?.dk);
      const preferred = [...pos, ...neu, ...neg, ...dk];
      const rank = new Map(preferred.map((k, i) => [String(k), i]));
      return [...keys].sort((a, b) => {
        const ra = rank.has(String(a)) ? rank.get(String(a)) : Number.MAX_SAFE_INTEGER;
        const rb = rank.has(String(b)) ? rank.get(String(b)) : Number.MAX_SAFE_INTEGER;
        if (ra !== rb) return ra - rb;
        return rankKey(b) - rankKey(a);
      });
    }
    const indexed = [...keys].map((k, i) => ({ k, i }));
    indexed.sort((a, b) => {
      const d = rankKey(b.k) - rankKey(a.k);
      if (Number.isFinite(d) && d !== 0) return d;
      return a.i - b.i;
    });
    return indexed.map((x) => x.k);
  };

  const isVoteIntention = String(currentVar?.label || "").toLowerCase().includes("vote intention");
  const isDontKnowOption = (key) => {
    const kk = String(key || "").toLowerCase();
    const lbl = String(currentVar?.labels?.[key] || currentVar?.full_labels?.[key] || "").toLowerCase();
    return kk.includes("dontknow") || lbl.includes("don't know") || lbl.includes("dont know");
  };
  const visibleVarKeys = (currentVar?.keys || []).filter((k) => !(isVoteIntention && isDontKnowOption(k)));

  const sectorScore = (svId) => {
    const sub = groupsById?.[svId];
    if (!sub) return -Infinity;
    const toArrLocal = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
    const pos = toArrLocal(sub?.polarity?.pos);
    const neg = toArrLocal(sub?.polarity?.neg);
    const sum = (keys) => keys.reduce((acc, k) => acc + (Number.isFinite(rankKey(k)) ? rankKey(k) : 0), 0);
    return sum(pos) - sum(neg);
  };

  const sortSubVarsByArea = (subVars = []) => {
    const indexed = [...subVars].map((sv, i) => ({ sv, i }));
    indexed.sort((a, b) => {
      const d = sectorScore(b.sv) - sectorScore(a.sv);
      if (Number.isFinite(d) && d !== 0) return d;
      return a.i - b.i;
    });
    return indexed.map((x) => x.sv);
  };

  const rankedLikertGridSubVars = sortSubVarsByArea(currentVar?.subVariables || []);
  const topLikertGridSubVar = rankedLikertGridSubVars[0] || firstLikertGridSubVar;
  const getTargetDefaultMetric = (subVarId) => {
    const sub = groupsById?.[subVarId];
    if (isWindfallGrid) {
      const ordered = sortKeysByArea(sub?.keys || []);
      return ordered[0] || sub?.keys?.[0] || null;
    }
    const pair = detectSentimentPair(sub);
    return pair.posToken;
  };

  const toggleVal = (curr, next, OG) => (curr === next ? OG : next);

  const getFallback = () => {
    if (currentVar?.["type"] === "likert") return "net";
    if (currentVar?.["type"] === "likert_grid") {
      if (ActionMode === "filter") {
        const pair = detectSentimentPair(groupsById?.[firstLikertGridSubVar]);
        return firstLikertGridSubVar ? `${firstLikertGridSubVar}|${pair.posToken}` : "compare";
      }
      return firstLikertGridSubVar || "compare";
    }
    return ActionMode === "explore" ? "top" : currentVar?.["keys"]?.[0];
  };

  React.useEffect(() => {
    if (
      currentVar?.type === "likert_grid" &&
      ActionMode === "filter" &&
      topLikertGridSubVar &&
      (!preferenceFilter || preferenceFilter === "compare")
    ) {
      const defaultMetric = getTargetDefaultMetric(topLikertGridSubVar);
      onPreferenceFilterChange?.(defaultMetric ? `${topLikertGridSubVar}|${defaultMetric}` : `${topLikertGridSubVar}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVar?.type, ActionMode, topLikertGridSubVar, preferenceFilter]);

  const optionBtnStyle = (isActive) => ({
    textTransform: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    width: "100%",
    height: "100%",
    minHeight: "3em",
    fontWeight: isActive ? 600 : 500,
    fontSize: "0.75rem",
    lineHeight: 1.2,
    whiteSpace: "normal",
    boxShadow: "none",
    borderRadius: 2,
    border: "1px solid",
    borderColor: isActive ? "primary.main" : "divider",
    color: isActive ? "primary.main" : "text.secondary",
    bgcolor: isActive ? "rgba(25, 118, 210, 0.08)" : "background.paper",
    py: 1,
    px: 1,
    '&:hover': {
      bgcolor: isActive ? "rgba(25, 118, 210, 0.15)" : "action.hover",
      borderColor: isActive ? "primary.main" : "text.primary",
    }
  });

  const SectionLabel = ({ title, subtitle }) => (
    <Box sx={{ mt: 2, mb: 1, px: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2, display: 'block', mt: 0.2 }}>
        {subtitle}
      </Typography>
    </Box>
  );

  if (!currentVar) return null;

  return (
    <Box sx={{ width: "100%", px: 0.5 }}>

      {/* ============================================================ */}
      {/* LIKERT LOGIC                                                 */}
      {/* ============================================================ */}
      {currentVar?.["type"] === "likert" && (
        <>
          <SectionLabel
            title="Summary Views"
            subtitle="Aggregated data calculations."
          />

          <Box display="grid" gridTemplateColumns={ActionMode === "explore" ? "1fr 1fr" : "1fr"} gap={0.75}>
            {ActionMode === "explore" && (
              <Button
                fullWidth
                variant="outlined"
                sx={optionBtnStyle(preferenceFilter === "top")}
                startIcon={<StarOutlineIcon sx={{ fontSize: 18 }} />}
                onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, "top", "top"))}
              >
                Top Option
              </Button>
            )}

            <Button
              fullWidth
              variant="outlined"
              sx={optionBtnStyle(preferenceFilter === "net")}
              startIcon={<EqualizerIcon sx={{ fontSize: 18 }} />}
              onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, "net", getFallback()))}
            >
              Net Score
            </Button>
          </Box>

          <Divider sx={{ my: 2, borderStyle: 'dashed' }} />

          <SectionLabel
            title="Individual Options"
            subtitle="Select a single option to isolate."
          />

          <Box
            display="grid"
            gridTemplateColumns="1fr 1fr"
            gap={0.75}
            alignItems="stretch"
          >
            {orderedKeys(visibleVarKeys).map((key) => {
              const isActive = preferenceFilter === key;
              return (
                <Tooltip
                  key={key}
                  title={currentVar?.["full_labels"]?.[key] || ""}
                  placement="left"
                  TransitionComponent={Fade}
                  arrow
                  disableHoverListener={!currentVar?.["full_labels"]?.[key]}
                >
                  <Button
                    sx={optionBtnStyle(isActive)}
                    onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, key, getFallback()))}
                  >
                    {currentVar?.["labels"]?.[key]}
                  </Button>
                </Tooltip>
              );
            })}
          </Box>
        </>
      )}

      {/* ============================================================ */}
      {/* CATEGORICAL LOGIC                                            */}
      {/* ============================================================ */}
      {currentVar?.["type"] === "categorical" && (
        <>
          {ActionMode === "explore" && (
            <>
              <SectionLabel
                title="Rankings"
                subtitle="Quickly view the highest performers."
              />
              <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={0.5}>
                {["top", "second", "third"].map((rank) => (
                  <Button
                    key={rank}
                    fullWidth
                    sx={optionBtnStyle(preferenceFilter === rank)}
                    onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, rank, "top"))}
                  >
                    {rank.charAt(0).toUpperCase() + rank.slice(1)}
                  </Button>
                ))}
              </Box>
              <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
            </>
          )}

          <SectionLabel
            title="Specific Categories"
            subtitle="Isolate a single category."
          />

          <Box
            display="grid"
            gridTemplateColumns="1fr 1fr"
            gap={0.75}
            alignItems="stretch"
          >
            {orderedKeys(visibleVarKeys).map((key) => {
              const isActive = preferenceFilter === key;
              return (
                <Tooltip
                  key={key}
                  title={currentVar?.["full_labels"]?.[key] || ""}
                  placement="left"
                  TransitionComponent={Fade}
                  arrow
                  disableHoverListener={!currentVar?.["full_labels"]?.[key]}
                >
                  <Button
                    sx={optionBtnStyle(isActive)}
                    onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, key, getFallback()))}
                  >
                    {currentVar?.["labels"]?.[key]}
                  </Button>
                </Tooltip>
              );
            })}
          </Box>
        </>
      )}

      {/* ============================================================ */}
      {/* LIKERT GRID LOGIC (multi-sector comparison)                 */}
      {/* ============================================================ */}
      {currentVar?.["type"] === "likert_grid" && (() => {
        const validSubVarIds = currentVar.subVariables || [];
        const isValidSubVar = validSubVarIds.includes((preferenceFilter || "").split("|")[0]);
        const isCompare = !preferenceFilter || preferenceFilter === "compare" || !isValidSubVar;
        const [activeSubVarId, activeSubKey] = isCompare ? [] : preferenceFilter.split("|");
        const activeSubVar = activeSubVarId ? groupsById?.[activeSubVarId] : null;
        const isTargetMode = ActionMode === "filter";

        return (
          <>
            {!isTargetMode && (
              <>
                <SectionLabel
                  title="View"
                  subtitle="Compare net trust across all businesses, or drill into one."
                />

                <Button
                  fullWidth
                  sx={optionBtnStyle(isCompare)}
                  startIcon={<EqualizerIcon sx={{ fontSize: 18 }} />}
                  onClick={() => onPreferenceFilterChange?.("compare")}
                >
                  Compare all businesses
                </Button>

                <Divider sx={{ my: 2, borderStyle: "dashed" }} />
              </>
            )}

            <SectionLabel
              title="Drill into a business"
              subtitle={isTargetMode ? "Select one business score for targeting." : "Select a business for individual results."}
            />

            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={0.75} alignItems="stretch">
              {sortSubVarsByArea(currentVar.subVariables || []).map((svId) => {
                const isActive = activeSubVarId === svId;
                return (
                  <Button
                    key={svId}
                    sx={optionBtnStyle(isActive)}
                    onClick={() =>
                      onPreferenceFilterChange?.(
                        isTargetMode
                          ? (() => {
                              const nextMetric = isActive
                                ? (isWindfallGrid
                                  ? (activeSubKey || getTargetDefaultMetric(svId))
                                  : currentLikertGridMetric)
                                : getTargetDefaultMetric(svId);
                              return nextMetric ? `${svId}|${nextMetric}` : svId;
                            })()
                          : svId
                      )
                    }
                  >
                    {currentVar.subLabels?.[svId] || svId}
                  </Button>
                );
              })}
            </Box>

            {isTargetMode && activeSubVarId && isWindfallGrid && activeSubVar && (
              <>
                <Divider sx={{ my: 2, borderStyle: "dashed" }} />
                <SectionLabel
                  title="Target response"
                  subtitle="Pick one response option for this sector."
                />
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={0.75} alignItems="stretch">
                  {orderedKeys(activeSubVar.keys || [], activeSubVar).map((key) => {
                    const isKeyActive = activeSubKey === key;
                    return (
                      <Button
                        key={key}
                        sx={optionBtnStyle(isKeyActive)}
                        onClick={() => onPreferenceFilterChange?.(`${activeSubVarId}|${key}`)}
                      >
                        {activeSubVar.labels?.[key] || key}
                      </Button>
                    );
                  })}
                </Box>
              </>
            )}

            {isTargetMode && activeSubVarId && !isWindfallGrid && (
              <>
                <Divider sx={{ my: 2, borderStyle: "dashed" }} />
                <SectionLabel
                  title="Target sentiment"
                  subtitle={`Pick ${sentimentPair.posLabel.toLowerCase()} or ${sentimentPair.negLabel.toLowerCase()} for this sector.`}
                />
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={0.75} alignItems="stretch">
                  <Button
                    sx={optionBtnStyle(currentLikertGridMetric === sentimentPair.posToken)}
                    onClick={() => onPreferenceFilterChange?.(`${activeSubVarId}|${sentimentPair.posToken}`)}
                  >
                    {sentimentPair.posLabel}
                  </Button>
                  <Button
                    sx={optionBtnStyle(currentLikertGridMetric === sentimentPair.negToken)}
                    onClick={() => onPreferenceFilterChange?.(`${activeSubVarId}|${sentimentPair.negToken}`)}
                  >
                    {sentimentPair.negLabel}
                  </Button>
                </Box>
              </>
            )}

            {!isTargetMode && activeSubVar && (
              <>
                <Divider sx={{ my: 2, borderStyle: "dashed" }} />
                <SectionLabel
                  title="Result type"
                  subtitle={activeSubVar.label}
                />

                <Button
                  fullWidth
                  sx={optionBtnStyle(!activeSubKey)}
                  startIcon={<EqualizerIcon sx={{ fontSize: 18 }} />}
                  onClick={() => onPreferenceFilterChange?.(activeSubVarId)}
                >
                  Net Score
                </Button>

                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={0.75} alignItems="stretch" sx={{ mt: 0.75 }}>
                  {orderedKeys(activeSubVar.keys || [], activeSubVar).map((key) => {
                    const isKeyActive = activeSubKey === key;
                    return (
                      <Button
                        key={key}
                        sx={optionBtnStyle(isKeyActive)}
                        onClick={() => onPreferenceFilterChange?.(activeSubVarId + "|" + key)}
                      >
                        {activeSubVar.labels?.[key] || key}
                      </Button>
                    );
                  })}
                </Box>
              </>
            )}
          </>
        );
      })()}

      {/* ============================================================ */}
      {/* PRICE ELASTICITY LOGIC                                       */}
      {/* ============================================================ */}
      {currentVar?.["type"] === "price_elast" && (
        <Box sx={{ mt: 1 }}>
          <SectionLabel
            title="Price Thresholds"
            subtitle="Standard elasticity definitions."
          />
          <Paper elevation={0} sx={{ bgcolor: 'action.hover', p: 0.5, borderRadius: 2 }}>
            <Stack direction={ActionMode === "explore" ? "row" : "column"} spacing={0.5}>
              {["expect", "expensive", "tooexpensive"].map((pol) => {
                const val = currentVar?.["polarity"]?.[pol];
                const isSelected = preferenceFilter === val;
                return (
                  <Tooltip
                    key={pol}
                    title={currentVar?.["full_labels"]?.[val] || ""}
                    placement="top"
                  >
                    <Button
                      fullWidth
                      sx={{
                        borderRadius: 1.5,
                        color: isSelected ? "text.primary" : "text.secondary",
                        bgcolor: isSelected ? "background.paper" : "transparent",
                        boxShadow: isSelected ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                        fontWeight: isSelected ? 600 : 500,
                        fontSize: "0.75rem",
                        textTransform: "none",
                        py: 0.8,
                        border: isSelected ? "1px solid" : "1px solid transparent",
                        borderColor: isSelected ? "divider" : "transparent",
                        '&:hover': { bgcolor: isSelected ? "background.paper" : "rgba(255,255,255,0.5)" }
                      }}
                      onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, val, currentVar?.["polarity"]?.expect))}
                    >
                      {currentVar?.["labels"]?.[val]}
                    </Button>
                  </Tooltip>
                );
              })}
            </Stack>
          </Paper>
        </Box>
      )}

    </Box>
  );
};

export default QuestionFilterComponent;
